import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Aurora } from "@/components/Decor";
import { BrontoWordmark } from "@/components/BrontoLogo";
import { Seo } from "@/lib/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_ENDPOINT = `${BACKEND_URL}/api/contact`;
const SUPPORT_EMAIL = "support@brontoplatforms.com";

/**
 * Support — public contact page. Submits to the local FastAPI backend
 * (/api/contact) which routes the message to Postmark → support@brontoplatforms.com
 * with Reply-To set to the sender's email so the team can respond directly.
 */
export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(CONTACT_ENDPOINT, form, { timeout: 20000 });
      toast.success("Message received. We will respond as soon as possible.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(detail || "Unable to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden" data-testid="support-page">
      <Seo
        title="Support & Contact — BrontoY"
        description="Contact the BrontoY team for support, partnerships, media requests, or account help. Messages are routed to support@brontoplatforms.com."
      />
      <Aurora variant="subtle" />

      <nav className="relative z-10 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <BrontoWordmark size="text-2xl" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="support-back-home"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} />
            Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-20 pb-16 px-6 lg:px-10 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-foreground/40 mb-6">
            Support
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-medium tracking-tighter leading-[0.95] mb-10 max-w-5xl">
            Get in <span className="rainbow-text">touch.</span>
          </h1>
          <p className="text-xl md:text-2xl text-foreground/65 max-w-3xl leading-relaxed font-light">
            For inquiries related to BrontoY — bugs, account issues, or feedback — please reach out using the form below.
          </p>
          <p className="mt-4 text-sm text-foreground/45">
            Messages are routed to{" "}
            <span className="text-foreground/70 font-medium">{SUPPORT_EMAIL}</span>.
          </p>
          <p className="mt-3 text-sm text-foreground/45">
            If you have detailed further queries you can also mail us directly at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="underline underline-offset-4 hover:text-foreground transition-colors"
              data-testid="support-email-link"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-6 lg:px-10 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-foreground/40 mb-3">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-foreground/5 border border-border rounded-xl px-5 py-4 text-foreground placeholder-foreground/30 focus:outline-none focus:border-foreground/30 transition-colors"
                  placeholder="Your full name"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-foreground/40 mb-3">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-foreground/5 border border-border rounded-xl px-5 py-4 text-foreground placeholder-foreground/30 focus:outline-none focus:border-foreground/30 transition-colors"
                  placeholder="you@example.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-foreground/40 mb-3">
                Message
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={7}
                className="w-full bg-foreground/5 border border-border rounded-xl px-5 py-4 text-foreground placeholder-foreground/30 focus:outline-none focus:border-foreground/30 transition-colors resize-none"
                placeholder="How can we help?"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Message <Mail className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 px-6 lg:px-10 border-t border-border">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-px bg-border rounded-3xl overflow-hidden">
          {[
            { label: "General", desc: "For general inquiries, partnerships, and information." },
            { label: "Media", desc: "For journalists, editors, and press representatives." },
            { label: "Careers", desc: "For career opportunities and recruitment." },
          ].map((item) => (
            <div key={item.label} className="bg-background p-8 md:p-10">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-foreground/40 mb-3">
                {item.label}
              </div>
              <p className="text-base text-foreground/70 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="py-10 px-6 border-t border-border text-center">
        <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
          BrontoY — By Bronto Intelligence
        </p>
      </footer>
    </div>
  );
}
